import { type FC } from 'react'
import { Tab } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { Tabs } from '../../../../components/Tabs/Tabs'
import { TimezoneSelector } from '../../../../components/TimezoneSelector/TimezoneSelector'
import { MeetingMode } from '../../../../models/Scheduling'
import { type BookingSlot } from './timeSlots'
import { SpecificTimePicker } from './SpecificTimePicker'
import styles from './SetTimeSection.module.scss'

export type TimeSelectionMode = 'specific-time' | 'client-chooses'

interface SetTimeSectionProps {
  mode: TimeSelectionMode
  eventTypeId: string
  userId?: string
  meetingMode: MeetingMode
  timezone: string
  locationTimezoneLabel: string
  selectedSlot: BookingSlot | null
  suggestedDate: Date | null
  onModeChange: (mode: TimeSelectionMode) => void
  onSlotChange: (slot: BookingSlot | null) => void
  onSuggestedDateChange: (date: Date | null) => void
  onTimezoneChange: (timezone: string) => void
}

// "Set the time": propose a specific time (calendar + slots) or send a booking link
export const SetTimeSection: FC<SetTimeSectionProps> = ({
  mode,
  eventTypeId,
  userId,
  meetingMode,
  timezone,
  locationTimezoneLabel,
  selectedSlot,
  suggestedDate,
  onModeChange,
  onSlotChange,
  onSuggestedDateChange,
  onTimezoneChange,
}) => (
  <div className='d-flex flex-column zocks-gap-0.5 position-relative z-1'>
    <label className='bold'>Set the time</label>
    <div className='border zocks-border-color-gray-200 zocks-border-1.0 overflow-hidden'>
      <Tabs activeKey={mode} onSelect={(key) => onModeChange((key as TimeSelectionMode) ?? 'specific-time')} className='pt-2 px-3'>
        <Tab eventKey='specific-time' title='Propose a specific time'>
          <div className='p-3'>
            <SpecificTimePicker eventTypeId={eventTypeId} userId={userId} selectedSlot={selectedSlot} onSlotChange={onSlotChange} />
          </div>
        </Tab>
        <Tab eventKey='client-chooses' title='Let the client choose'>
          <div className='d-flex flex-column zocks-gap-0.25 p-3'>
            <label className='bold'>Offering all open times</label>
            <div className='paragraph-small zocks-text-gray-600'>
              Clients browse your full availability and book any open slot. Set a date below so they will see this day&apos;s availability first when they open
              the booking page.
            </div>
            <div className='d-flex flex-column zocks-gap-0.25 mt-3'>
              <label className='bold'>
                Suggested date <span className='zocks-text-gray-500'>(optional)</span>
              </label>
              <DatePicker
                selected={suggestedDate}
                minDate={new Date()}
                calendarStartDay={1}
                onChange={onSuggestedDateChange}
                showIcon
                toggleCalendarOnIconClick
                icon='bi bi-calendar4-week'
                calendarIconClassname={styles.calendarIcon}
                placeholderText='Select a date'
                className='form-control'
                wrapperClassName='w-100'
              />
            </div>
          </div>
        </Tab>
      </Tabs>
    </div>
    {mode === 'specific-time' ? (
      <div className='d-flex flex-column zocks-gap-0.25 mt-2'>
        <label className='bold'>Timezone</label>
        {meetingMode === MeetingMode.IN_PERSON ? (
          <div className='d-flex align-items-center justify-content-between form-control zocks-bg-color-gray-100 zocks-text-gray-500'>
            <span>{locationTimezoneLabel || 'Set location timezone above'}</span>
            <span className='d-inline-flex align-items-center zocks-gap-0.25'>
              <i className='bi bi-lock' aria-hidden='true' />
              Set by location
            </span>
          </div>
        ) : (
          <TimezoneSelector initialSelectedTimezone={timezone} onTimezoneSelected={(value) => onTimezoneChange(value ?? '')} />
        )}
      </div>
    ) : null}
  </div>
)
